// What the Exportar imagem dialog decides from the master's four checks and the board's own state; the controller only wires it to the DOM.
import type { LiveNote, LiveScene, LiveToken } from "../types/generated/live";
import { markOf } from "./edges";

export const CHECKS = ["fog", "marks", "hidden", "notes"] as const;
export type Check = (typeof CHECKS)[number];
export type Checks = Record<Check, boolean>;
export type Verdict = "same" | "off_table" | "master";

// A code that carries a master-only switch: a secret door, window or arrow slit, a false door, a locked door.
const switched = (code: string) => {
	const mark = markOf(code);
	return Boolean(mark?.secret || mark?.locked || mark?.fake);
};

export const noChecks = (): Checks => ({ fog: false, marks: false, hidden: false, notes: false });

export const anyChecked = (checks: Checks) => CHECKS.some((check) => checks[check]);

// Who the picture is for: the players' view, how they will see a map that is not on the table yet, or the master alone.
export function verdict(checks: Checks, onTable: boolean): Verdict {
	if (anyChecked(checks)) return "master";
	return onTable ? "same" : "off_table";
}

// "a, b and c", joined by the reader's own locale.
export function revealList(checks: Checks, labels: Record<Check, string>, locale: string) {
	const names = CHECKS.filter((check) => checks[check]).map((check) => labels[check]);
	return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(names);
}

// The master's request for the picture's snapshot: a param for each check that is on, none for the rest.
export function exportUrl(base: string, checks: Checks) {
	const url = new URL(base, location.href);
	for (const check of CHECKS) if (checks[check]) url.searchParams.set(check, "1");
	return url.pathname + url.search;
}

type Board = { scene?: Partial<LiveScene>; tokens?: Record<string, Partial<LiveToken>> | Partial<LiveToken>[]; notes?: Record<string, LiveNote> | LiveNote[] };

const some = <T>(collection: Record<string, T> | T[] | undefined, test: (item: T) => boolean) => Object.values(collection ?? {}).some(test);

// Which checks would change nothing on this map, read from the master's own board; the picture itself always comes from the server.
export function unavailable(board: Board): Check[] {
	const edges = (board.scene?.edges ?? {}) as Record<string, string[] | undefined>;
	const none: Check[] = [];
	if (!(board.scene?.fog as { enabled?: boolean } | undefined)?.enabled) none.push("fog");
	if (!Object.values(edges).some((rows) => rows?.some((row) => [...row].some(switched)))) none.push("marks");
	if (!some(board.tokens, (token) => Boolean(token.hidden))) none.push("hidden");
	if (!some(board.notes, () => true)) none.push("notes");
	return none;
}

// Which buttons the footer shows: a phone that can share files shares, and a phone that cannot downloads; a computer copies, where the browser
// can, and downloads.
export function footer({ coarse, canShare, canCopy }: { coarse: boolean; canShare: boolean; canCopy: boolean }) {
	return { share: coarse && canShare, copy: !coarse && canCopy, download: !(coarse && canShare) };
}

export const fill = (text: string, values: Record<string, string | number>) => text.replace(/%\{(\w+)\}/g, (_, key: string) => String(values[key]));
